import { useCallback, useEffect, useMemo, useState } from "react";

import * as reactiveUtils from "@arcgis/core/core/reactiveUtils";
import Layer from "@arcgis/core/layers/Layer";
import { useDispatch, useSelector } from "react-redux";
import { setPanelPath_actionType } from "../../../redux/constants";
import { sendMessageToSW } from "../../../serviceWorker";
import {
  getLayerFromId,
  graphicsLayer,
  removePoint,
  view,
} from "../../../utils/API";
import { convertFileToBase64, getConfigISO } from "../../../utils/helper";
import { fetchReports } from "../../../utils/LC_API";
import { Modal } from "../../UI";
import PacReport from "./PacReport/PacReport";
import { StyledReportsModal, StyledReportsWrapper } from "./Reports-styled";
import ReportSelectionFlow from "./ReportSelectionFlow";
import ReportsHeader from "./ReportsHeader/ReportsHeader";
import WarehouseReport from "./WarehouseReport/WarehouseReport";

const Reports = ({ config, openSnackbar, t, activeLanguage }) => {
  const [layer, setLayer] = useState(null);
  const [feature, setFeature] = useState(null);
  const activeModule = useSelector((state) => state.activeModule);
  const panelPath = useSelector((state) => state.panelPath);

  const [report, setReport] = useState("reportAll");
  const dispatch = useDispatch();

  useEffect(() => {
    if (!view) return;

    if (report === "pac" || report === "warehouse") {
      const layerId =
        report === "pac" ? config.reportingLayerId : config.warehouseSurveyId;
      let layerPromise = getLayerFromId(Layer, layerId);
      layerPromise.then((layerPreLoad) => {
        layerPreLoad.load().then((reportLayer) => {
          setLayer(reportLayer);
        });
      });
    }
  }, [report]);

  useEffect(() => {
    if (!view) return;

    setTimeout(() => {
      /** Push report an update button on the popup */
      reactiveUtils.on(
        () => view.popup,
        "trigger-action",
        (event) => {
          if (
            event.action.id !== "report-update" ||
            !view.popup.selectedFeature
          )
            return;

          const selectedFeature = view.popup.selectedFeature;
          if (selectedFeature?.geometry?.type !== "point") {
            selectedFeature.geometry = view.popup.location;
          }

          view.closePopup();
          //Somehow clustered features don't specify layer, but they have a sourceLayer attribute
          if (
            (!selectedFeature.layer ||
              selectedFeature.layer.declaredClass !==
                "esri.layers.FeatureLayer") &&
            selectedFeature.sourceLayer
          )
            selectedFeature.layer = selectedFeature.sourceLayer;
          setFeature(selectedFeature);
          setReport("pac");
          dispatch({ type: setPanelPath_actionType, payload: "reportAll" });
        }
      );
    }, 100);

    if (!panelPath || report === "pac") return;
    const reports =
      config.modules[activeModule]?.reports ??
      config.opDefaults.modules[activeModule]?.reports ??
      [];
    if (reports.length === 1) {
      setReport(reports[0]);
    } else {
      setReport(panelPath);
    }
  }, [panelPath, activeLanguage]);

  const hideReport = useMemo(
    () => !panelPath || panelPath === "" || panelPath !== "reportAll",
    [panelPath]
  );

  const removeExistingPoint = useCallback((graphicsLayer) => {
    if (graphicsLayer?.graphics) {
      const existingPoint = graphicsLayer.graphics.find(
        (g) => g.id === "report-point"
      );
      if (existingPoint) {
        graphicsLayer.remove(existingPoint);
      }
    }
  }, []);

  if (hideReport) return null;

  const closeModal = () => {
    dispatch({ type: setPanelPath_actionType, payload: "" });
    setFeature(null);
    setLayer(null);
    setReport("reportAll");

    const newURL = new URL(window.location.href);
    const newUrlParams = new URLSearchParams(window.location.search);
    newUrlParams.delete("datacollection", "open");
    newUrlParams.delete("module", activeModule.toLowerCase());
    newUrlParams.delete("lang", activeLanguage.toLowerCase());
    newURL.search = newUrlParams;
    window.history.replaceState({ path: newURL.href }, "", newURL.href);
    removeExistingPoint(graphicsLayer);
  };

  const handleSendReportApi = async (contactsData, reportData) => {
    try {
      const { geometry, attachment, ...restData } = reportData;

      const requestData = {
        // predefined fields
        showpublic: 2,
        opid: config.id,
        currvalidationfield: 1,
        isdeleted: 2,
        reportedfrom: 2,

        // contacts information
        fpname: contactsData.firstName + " " + contactsData.surName,
        fpemail: contactsData.email,
        fpphone: contactsData.contactNumber,
        fporganisation: contactsData.organization,

        //selected fields
        currvehtype: restData.currvehtype,
        pactype: restData.pactype,
        reportedremarks: restData.reportedremarks,
        reporttype: restData.reporttype,
        reporton: restData.reporton,
        reportedlayerid: restData.reportedlayerid,
        reportedlayername: restData.reportedlayername,
        reportedfeatureid: restData.reportedfeatureid,

        // location
        latitude: geometry.latitude,
        longitude: geometry.longitude,
        attachment_1: null,
        attachment_2: null,
        attachment_3: null,
      };

      if (Array.isArray(attachment)) {
        for (const index in attachment) {
          const file = attachment[index];
          const base64string = await convertFileToBase64(file);
          if (base64string) {
            const attachmentIndex = parseInt(index) + 1;
            requestData[`attachment_${attachmentIndex}`] = base64string;
          }
        }
      }

      await sendReport(requestData);
    } catch (error) {
      console.error("Error sending report:", error);
      throw error;
    }
  };

  const sendReport = async (data) => {
    try {
      const body = {
        body: data,
      };
      const response = await fetchReports(body);

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`API Error (${response.status}): ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error sending report:", error);
      throw error;
    }
  };

  const handleSubmit = async (contactsData, reportForm) => {
    try {
      if (report === "pac") {
        await handleSendReportApi(contactsData, reportForm);
      } else {
        const { attachment, geometry, ...reportAttributes } = reportForm;
        const reportData = {
          attributes: {
            ...reportAttributes,
            fpname: contactsData.firstName + " " + contactsData.surName,
            fpemail: contactsData.email,
            fpphone: contactsData.contactNumber,
            fporganisation: contactsData.organization,
            showpublic: 2,
            opid: config.id,
            currvalidationfield: 1,
            isdeleted: 2,
            longitude: geometry.longitude,
            latitude: geometry.latitude,
          },
          geometry: {
            type: "point",
            longitude: geometry.longitude,
            latitude: geometry.latitude,
          },
        };

        /**
         * Temporary patch: use iso code, use the first one when more iso codes are specified
         */
        const iso3 = getConfigISO(config);
        if (iso3) {
          reportData.attributes.iso3 = Array.isArray(iso3) ? iso3[0] : iso3;
        }

        // sending form data to service worker for offline
        sendMessageToSW({ report_form_data: { reportData } });

        const editResults = await layer.applyEdits({
          addFeatures: [reportData],
        });

        if (attachment && editResults.addFeatureResults) {
          let attributes = {};
          attributes[layer.objectIdField] =
            editResults.addFeatureResults[0].objectId;
          const feature = { attributes: attributes };
          const promises = [];

          attachment.forEach((file) => {
            const attachmentForm = new FormData();
            attachmentForm.set("attachment", file);

            const promise = layer.addAttachment(feature, attachmentForm);
            promises.push(promise);
          });

          await Promise.all(promises);
        }
      }

      openSnackbar(t("screen.message.reportSuccess"), 5000);
      removeExistingPoint(graphicsLayer);
    } catch (error) {
      console.error(error);
      openSnackbar(t("screen.message.error"), 5000);
      await removePoint();
      throw new error();
    }
  };

  const openReport = (report) => {
    setReport(report);
  };

  const renderReport = () => {
    const reportTitlePath = `${config.title} / ${activeModule} / `;
    const reportTitle =
      reportTitlePath + t("report." + report + ".title") ??
      config.reportDefinition[report]?.titleLabel;
    switch (report) {
      case "pac": {
        return (
          <PacReport
            config={config}
            closeModal={closeModal}
            layer={layer}
            feature={feature}
            setFeature={setFeature}
            openSnackbar={openSnackbar}
            onSubmit={handleSubmit}
            openReport={() => openReport("reportAll")}
            title={reportTitle}
            t={t}
          />
        );
      }
      case "warehouse": {
        return (
          <WarehouseReport
            config={config}
            closeModal={closeModal}
            layer={layer}
            feature={feature}
            openSnackbar={openSnackbar}
            onSubmit={handleSubmit}
            openReport={() => openReport("reportAll")}
            title={reportTitle}
            t={t}
          />
        );
      }
      case "reportAll": {
        return (
          <ReportSelectionFlow
            closeModal={closeModal}
            openReport={openReport}
            config={config}
            activeModule={activeModule}
            t={t}
            openSnackbar={openSnackbar}
          />
        );
      }
      default: {
        return (
          <>
            <ReportsHeader
              title={reportTitle}
              close={closeModal}
              onArrowClick={() => openReport("reportAll")}
              showArrow={true}
            />
            <iframe
              width="100%"
              height="100%"
              src={config.reportDefinition[report]?.url[activeLanguage]}
            />
          </>
        );
      }
    }
  };

  return (
    <Modal show={!hideReport}>
      <StyledReportsWrapper>
        <StyledReportsModal>{renderReport()}</StyledReportsModal>
      </StyledReportsWrapper>
    </Modal>
  );
};

export default Reports;
